/**
 * PlatformConfiguration: MSP integration config page (credentials, permissions,
 * RMM automation availability and remote-session configuration).
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LayeredAutocompleteSelect from "components/LayeredAutocompleteSelect";
import ArrowBackIcon from "../assets/img/LeftIcon.svg";
import RemoveIcon from "../assets/img/RemoveIcon.svg";
import AddIcon from "../assets/img/AddIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import WarningInfo from "../assets/img/warning_info.svg";
import IntegrationTable from "components/IntegrationTable";
import IngestionRunDetails, {
  formatIngestionStatusLabel,
  IngestionRunDetailsPayload,
  readIngestionDetails,
} from "components/IngestionRunDetails";
import NeoConfigModal, { ConfigSelectionItem } from "components/NeoConfigModal";
import { usePlatforms } from "hooks/useIntegration";
import { slugify } from "utils/stringUtils";
import { getConnectorLogo, formatAppName } from "../utils/connectorUtils";
import {
  IT_GLUE_DEFAULT_BASE_URL,
  getPlatformConfigSchemaFields,
  sherwebDefaultConfig,
} from "../utils/constants";
import { Platform } from "types/integration";
import { useIntegration } from "hooks/useIntegration";
import { useUserMsp } from "hooks/useUserMsp";
import { useNinjaOneRefreshToken } from "../hooks/useNinjaOneRefreshToken";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";
import { isAutotask, isConnectWise, isHalo } from "../utils/mspUtils";
import IntegrationApi, { RmmAutomation } from "apis/integrationApi";
import HaloPermissionFeedbackAlert from "../components/HaloPermissionFeedbackAlert";
import ItGlueAccessFeedbackAlert from "../components/ItGlueAccessFeedbackAlert";
import DattoScriptExportDialog from "../components/DattoScriptExportDialog";
import DattoAccessFeedbackAlert from "../components/DattoAccessFeedbackAlert";
import { CollapsibleSetupGuidePanel } from "../components/integrationGuides/CollapsibleSetupGuidePanel";
import { SherwebSetupGuideSteps } from "../components/integrationGuides/SherwebSetupGuideSteps";
import { DattoRmmGuideInner } from "../components/integrationGuides/DattoRmmGuideInner";
import {
  HaloPermissionFeedback,
  fetchHaloPermissionFeedback,
} from "../utils/haloPermissionChecks";
import {
  ItGlueAccessFeedback,
  buildItGlueAccessFeedback,
} from "../utils/itGlueAccessChecks";

const DATTO_REVERSE_SSH_WINDOWS_COMPONENT_UID = "reverse_ssh_windows_component_uid";
const DATTO_REVERSE_SSH_MAC_COMPONENT_UID = "reverse_ssh_mac_component_uid";
const NINJAONE_REVERSE_SSH_WINDOWS_SCRIPT_ID = "reverse_ssh_windows_script_id";
const NINJAONE_REVERSE_SSH_MAC_SCRIPT_ID = "reverse_ssh_mac_script_id";
const RMM_DISABLED_SCRIPT_COMPONENTS_KEY = "disabled_script_components";

const mapRmmAutomationsToListItems = (automations: RmmAutomation[]): ConfigSelectionItem[] =>
  automations.map((automation) => ({
    name: automation.name,
    groupId: automation.uid,
    description: automation.description,
    operatingSystems: automation.operatingSystems,
    type: automation.type,
  }));

const listRmmAutomations = async (provider: "datto" | "ninjaone", platformId: string): Promise<RmmAutomation[]> => {
  const api = new IntegrationApi();
  return provider === "datto"
    ? (await api.listDattoScriptComponents(platformId)).components
    : (await api.listNinjaOneAutomations(platformId)).automations;
};

const styles = {
  pageContainer: {
    p: "24px 32px",
    background: "#F9FAFB",
    flexGrow: 1,
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: "14px",
    mr: "4px",
  },
  headerInfoContainer: {
    display: "flex",
    alignItems: "center",
  },
  backButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    minHeight: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 1,
    p: 0,
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  headerTitle: {
    fontWeight: 700,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    color: "#000",
  },
  headerSubtitle: {
    color: "#666F8D",
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    fontWeight: 500,
  },
  logo: {
    height: "57px",
  },
  paperContainer: {
    borderRadius: 3,
    overflow: "hidden",
    border: "1px solid #E5E7EB",
    boxShadow: "none",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: "21px 25px 24px",
    backgroundColor: "#FFF",
    cursor: "pointer",
  },
  sectionTitle: {
    fontWeight: 700,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    color: "#5A5E73",
  },
  label: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    fontFamily: "var(--theo-font-sans)",
    mb: "6px",
  },
  textFieldWrapper: {
    height: "48px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
  },
  textFieldInput: {
    height: "100%",
    padding: "13px 16px",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "var(--theo-font-sans)",
    color: "#000",
    boxSizing: "border-box",
  },
  dattoComponentSelectWrapper: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 500,
      fontFamily: "var(--theo-font-sans)",
      color: "#000",
      backgroundColor: "#FFF",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E8F0",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#CBD5E1",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#705D4B",
    },
    "& .MuiAutocomplete-input": {
      textOverflow: "ellipsis",
    },
  },
  expandButton: {
    backgroundColor: "#F3F4F6",
    borderRadius: "50%",
  },
  configContent: {
    p: "24px",
    backgroundColor: "#FFF",
    borderTop: "1px solid #E5E7EB",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 3,
    mb: 3,
  },
  footerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  connectionChip: {
    backgroundColor: "#D9FFDD",
    color: "#000",
    fontWeight: 500,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "16px",
    height: "38px",
    flexShrink: 0,
    borderRadius: "6px",
    border: "1px solid #7AF086",
    justifyContent: "flex-start",
  },
  testButton: {
    mr: 1,
    textTransform: "none",
    backgroundColor: "#705D4A",
    color: "#FFF",
    borderRadius: "6px",
    minWidth: "85px",
    height: "40px",
    padding: "8px 16px",
    gap: "4px",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#5e4d3c",
    },
  },
  nextButton: {
    backgroundColor: "#F5F5F5",
    color: "#232B44",
    textTransform: "none",
    borderRadius: 2,
    minWidth: "85px",
    height: "40px",
    padding: "8px 16px",
    gap: "4px",
    flexShrink: 0,
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
};

type RmmRemoteSessionScriptSelectProps = {
  /** OS label shown above the field ("Windows" or "macOS"). */
  label: string;
  value: string;
  onChange: (uid: string) => void;
  /** Live catalog of Datto components / NinjaOne scripts. */
  scripts: ConfigSelectionItem[];
  /** Previously saved scripts, used when the selected uid is missing from the live catalog. */
  storedScripts: ConfigSelectionItem[];
  disabled?: boolean;
};

/**
 * RMM remote-session script picker (Windows / macOS) for Datto and NinjaOne.
 * Type-to-filter Autocomplete over the live script catalog.
 */
const RmmRemoteSessionScriptSelect: React.FC<RmmRemoteSessionScriptSelectProps> = ({
  label,
  value,
  onChange,
  scripts,
  storedScripts,
  disabled = false,
}) => {
  const selectedUid = value.trim();
  const scriptOptions = useMemo(() => {
    const toOption = (script: ConfigSelectionItem) => ({
      label: script.name,
      value: script.groupId,
      searchText: [script.name, script.groupId, script.description].filter(Boolean).join(" "),
    });
    const catalogOptions = scripts.filter((script) => script.groupId).map(toOption);
    const selectedUidInCatalog = catalogOptions.some((option) => option.value === selectedUid);
    if (!selectedUid || selectedUidInCatalog) {
      return catalogOptions;
    }
    const storedScript = storedScripts.find((script) => script.groupId === selectedUid);
    const storedName = storedScript?.name || `Current: ${selectedUid}`;
    return [toOption({ name: storedName, groupId: selectedUid, description: storedScript?.description }), ...catalogOptions];
  }, [scripts, storedScripts, selectedUid]);

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151", mb: 0.75, fontFamily: "var(--theo-font-sans)" }}>
        {label}
      </Typography>
      <Box sx={styles.dattoComponentSelectWrapper}>
        <LayeredAutocompleteSelect
          label=""
          inputAriaLabel={`${label} secure shell script`}
          placeholder="Search scripts"
          value={value}
          onChange={onChange}
          options={scriptOptions}
          disabled={disabled}
          matchFrom="any"
          noOptionsText="No matching scripts"
          textFieldSx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& legend": { display: "none" },
          }}
        />
      </Box>
    </Box>
  );
};

const PlatformConfiguration: React.FC = () => {
  const { appName, customerId } = useParams();
  const navigate = useNavigate();
  const [configOpen, setConfigOpen] = useState(true);
  const [permissionsOpen, setPermissionsOpen] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [haloPermissionFeedback, setHaloPermissionFeedback] =
    useState<HaloPermissionFeedback | null>(null);
  const [itGlueAccessFeedback, setItGlueAccessFeedback] =
    useState<ItGlueAccessFeedback | null>(null);
  const [sherwebInstructionsExpanded, setSherwebInstructionsExpanded] = useState(false);
  const [dattoInstructionsExpanded, setDattoInstructionsExpanded] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    platforms,
    platformActions,
    platformActionsLoading,
    error,
    getPlatformActions,
    getPlatforms,
    updatePlatformActionPermission,
    testPlatformConfiguration,
    updatePlatformConfig,
  } = usePlatforms();
  const { userMspInfo } = useUserMsp();

  const { testResults, getPlatformConfig, clearTestResultEntry } = useIntegration();
  
  const [fetchedConfig, setFetchedConfig] = useState<Record<string, any> | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScriptUids, setSelectedScriptUids] = useState<string[]>([]);
  const [modalReverseSshWindowsUid, setModalReverseSshWindowsUid] = useState("");
  const [modalReverseSshMacUid, setModalReverseSshMacUid] = useState("");
  const [allScripts, setAllScripts] = useState<ConfigSelectionItem[]>([]);
  const selectionRequestRef = useRef(0);
  const [rmmScriptsForDisplay, setRmmScriptsForDisplay] = useState<ConfigSelectionItem[]>([]);
  const [scriptExportDialogOpen, setScriptExportDialogOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<boolean | null>(null);

  const [ingestionsOpen, setIngestionsOpen] = useState(true);
  const [ingestionStatus, setIngestionStatus] = useState<string | null>(null);
  const [ingestionDetails, setIngestionDetails] = useState<IngestionRunDetailsPayload | null>(null);
  const [ingestionCompletedAt, setIngestionCompletedAt] = useState<string | null>(null);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [isTriggeringIngestion, setIsTriggeringIngestion] = useState(false);

  const platform = useMemo(() => {
    if (!appName) return null;

    const platformIdMatch = appName.match(/^(\d+)-(.+)$/);

    if (platformIdMatch) {
      const [, platformId] = platformIdMatch;
      return platforms.find((p: Platform) => p.id.toString() === platformId);
    } else {
      return platforms.find((p: Platform) => slugify(p.name) === appName);
    }
  }, [platforms, appName]);

  const platformNameLower = useMemo(
    () => String(platform?.name || "").toLowerCase(),
    [platform?.name]
  );
  const platformTypeLower = useMemo(
    () => String(platform?.type || "").toLowerCase(),
    [platform?.type]
  );
  const isRmmPlatform = platformTypeLower === "rmm";
  const isNinjaOnePlatform = platformNameLower === "ninjaone";

  // Check if this is a PSA platform that has queues.
  const isPsaPlatform = useMemo(() => {
    if (platformTypeLower !== "psa") return false;
    return ['autotask', 'superops', 'halo', 'connectwise', 'ninjaone'].includes(platformNameLower);
  }, [platformNameLower, platformTypeLower]);
  const isAutotaskPlatform = useMemo(
    () => platformTypeLower === "psa" && isAutotask(platformNameLower),
    [platformNameLower, platformTypeLower]
  );
  const isHaloPlatform = useMemo(
    () => platformTypeLower === "psa" && isHalo(platformNameLower),
    [platformNameLower, platformTypeLower]
  );
  const isConnectWisePlatform = useMemo(
    () => platformTypeLower === "psa" && isConnectWise(platformNameLower),
    [platformNameLower, platformTypeLower]
  );
  const isItGluePlatform = useMemo(() => {
    return platformNameLower === "itglue" || platformNameLower === "it glue";
  }, [platformNameLower]);
  const isSherwebPlatform = useMemo(
    () => platformNameLower === "sherweb",
    [platformNameLower]
  );
  const isDattoPlatform = useMemo(
    () => isRmmPlatform && platformNameLower === "datto",
    [isRmmPlatform, platformNameLower]
  );
  const isNinjaOneRmmPlatform = isRmmPlatform && isNinjaOnePlatform;
  const supportsRmmScriptSelection = isDattoPlatform || isNinjaOneRmmPlatform;
  const rmmScriptProviderLabel = isNinjaOneRmmPlatform ? "NinjaOne" : "Datto";
  const rmmAutomationLabelPlural = isNinjaOneRmmPlatform ? "automations" : "scripts";

  useEffect(() => {
    selectionRequestRef.current += 1;
    setModalOpen(false);
    setModalLoading(false);
    setAllScripts([]);
  }, [platform?.id]);

  const ingestionSourceDocType = useMemo(() => {
    if (platformNameLower === "hudu") return "hudu_articles";
    if (platformNameLower === "itglue" || platformNameLower === "it glue") return "itglue_docs";
    if (isAutotaskPlatform || isHaloPlatform || isConnectWisePlatform) return "psa_tickets";
    return null;
  }, [platformNameLower, isAutotaskPlatform, isHaloPlatform, isConnectWisePlatform]);
  const supportsIngestions = Boolean(ingestionSourceDocType);
  const ingestionCardTitle =
    ingestionSourceDocType === "hudu_articles"
      ? "Hudu articles"
      : ingestionSourceDocType === "psa_tickets"
      ? "Tickets"
      : "IT Glue documents";
  const ingestionButtonLabel =
    ingestionSourceDocType === "hudu_articles"
      ? "Refresh Articles"
      : ingestionSourceDocType === "psa_tickets"
      ? "Refresh Tickets"
      : "Refresh Documents";

  /** Horizon `platform.id` for `ingestion_metadata.platform_id` (required for psa_tickets). */
  const ingestionPlatformId = useMemo(() => {
    if (ingestionSourceDocType === "psa_tickets" && platform?.id != null) {
      return Number(platform.id);
    }
    return undefined;
  }, [ingestionSourceDocType, platform?.id]);

  useEffect(() => {
    if (platform?.id) {
      getPlatformActions(platform.id.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform?.id]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (platform?.id) {
        try {
          // Always fetch config via API to get metadata.queues merged in
          const config = await getPlatformConfig(platform.id.toString());
          setFetchedConfig(config || {});
        } catch {
          setFetchedConfig({});
        }
      } else {
        setFetchedConfig(null);
      }
    };
    fetchConfig();
  }, [platform?.id, getPlatformConfig]);

  const safeActions = Array.isArray(platformActions)
    ? platformActions
    : Array.isArray(platformActions?.rows)
    ? platformActions.rows
    : [];

  const configSchema = useMemo(() => {
    if (!platform?.name) return [];
    return getPlatformConfigSchemaFields(platform.name);
  }, [platform?.name]);
  const formConfigFields = configSchema;

  const configData = useMemo(() => {
    if (isSherwebPlatform) {
      return { ...sherwebDefaultConfig, ...(fetchedConfig || {}) };
    }
    return fetchedConfig || {};
  }, [fetchedConfig, isSherwebPlatform]);

  useEffect(() => {
    const fetchIngestionStatus = async () => {
      if (!supportsIngestions || !ingestionSourceDocType) return;
      setIngestionLoading(true);
      try {
        const integrationApi = new IntegrationApi();
        const result = await integrationApi.getIngestionStatus(
          ingestionSourceDocType as "hudu_articles" | "itglue_docs" | "psa_tickets",
          ingestionPlatformId,
          userMspInfo?.msp_id
        );
        setIngestionStatus(result?.ingestion?.status || null);
        setIngestionDetails(readIngestionDetails(result?.ingestion));
        setIngestionCompletedAt(result?.ingestion?.completed_at || null);
      } catch {
        setIngestionStatus(null);
        setIngestionDetails(null);
        setIngestionCompletedAt(null);
      } finally {
        setIngestionLoading(false);
      }
    };
    fetchIngestionStatus();
  }, [supportsIngestions, ingestionSourceDocType, ingestionPlatformId, userMspInfo?.msp_id]);

  useEffect(() => {
    if (!supportsIngestions || !ingestionSourceDocType) return;
    const statusText = String(ingestionStatus || "").toUpperCase();
    if (!statusText.startsWith("RUNNING")) return;

    const interval = setInterval(async () => {
      try {
        const integrationApi = new IntegrationApi();
        const result = await integrationApi.getIngestionStatus(
          ingestionSourceDocType as "hudu_articles" | "itglue_docs" | "psa_tickets",
          ingestionPlatformId,
          userMspInfo?.msp_id
        );
        setIngestionStatus(result?.ingestion?.status || null);
        setIngestionDetails(readIngestionDetails(result?.ingestion));
        setIngestionCompletedAt(result?.ingestion?.completed_at || null);
      } catch {
        // keep silent while polling
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [supportsIngestions, ingestionSourceDocType, ingestionPlatformId, ingestionStatus, userMspInfo?.msp_id]);

  // Check if platform is configured (has credentials)
  const isPlatformConfigured = useMemo(() => {
    if (!platform) return false;
    // Check if platform has basic configuration (endpoint or token_key)
    return !!(platform.endpoint || platform.token_key || (configData && Object.keys(configData).length > 0));
  }, [platform, configData]);

  const remoteSessionScriptConfigKeys = useMemo(() => {
    if (isDattoPlatform) {
      return {
        windows: DATTO_REVERSE_SSH_WINDOWS_COMPONENT_UID,
        mac: DATTO_REVERSE_SSH_MAC_COMPONENT_UID,
      };
    }
    if (isNinjaOneRmmPlatform) {
      return {
        windows: NINJAONE_REVERSE_SSH_WINDOWS_SCRIPT_ID,
        mac: NINJAONE_REVERSE_SSH_MAC_SCRIPT_ID,
      };
    }
    return null;
  }, [isDattoPlatform, isNinjaOneRmmPlatform]);
  const remoteSessionWindowsScriptUid = useMemo(
    () => String(remoteSessionScriptConfigKeys ? configData[remoteSessionScriptConfigKeys.windows] || "" : "").trim(),
    [configData, remoteSessionScriptConfigKeys]
  );
  const remoteSessionMacScriptUid = useMemo(
    () => String(remoteSessionScriptConfigKeys ? configData[remoteSessionScriptConfigKeys.mac] || "" : "").trim(),
    [configData, remoteSessionScriptConfigKeys]
  );
  const rmmDisabledScriptUids = useMemo(() => {
    if (!Array.isArray(configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY])) {
      return [] as string[];
    }
    return (configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY] as Array<{ uid?: string }>)
      .map((entry) => String(entry.uid || "").trim())
      .filter(Boolean);
  }, [configData]);

  useEffect(() => {
    let canceled = false;
    const fetchRmmScriptsForDisplay = async () => {
      if (!supportsRmmScriptSelection || !platform?.id || !isPlatformConfigured) {
        setRmmScriptsForDisplay([]);
        return;
      }

      try {
        const automations = await listRmmAutomations(isDattoPlatform ? "datto" : "ninjaone", platform.id.toString());
        if (canceled) return;
        setRmmScriptsForDisplay(mapRmmAutomationsToListItems(automations));
      } catch (error) {
        if (canceled) return;
        console.error(`Failed to fetch ${rmmScriptProviderLabel} ${rmmAutomationLabelPlural} for display:`, error);
        setRmmScriptsForDisplay([]);
      }
    };

    fetchRmmScriptsForDisplay();
    return () => { canceled = true; };
  }, [
    isDattoPlatform,
    supportsRmmScriptSelection,
    platform?.id,
    isPlatformConfigured,
    rmmScriptProviderLabel,
    rmmAutomationLabelPlural,
  ]);

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [testSucceededAfterEdit, setTestSucceededAfterEdit] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<Record<string, string>>({});
  const [lastPlatformId, setLastPlatformId] = useState<string | null>(null);

  useEffect(() => {
    const currentPlatformId = platform?.id?.toString() || null;

    // Initialize when config exists, or for connectors with safe defaults when API returns {}.
    const canInitItGlueEmpty = isItGluePlatform && configSchema.length > 0;
    const canInitSherwebEmpty = isSherwebPlatform && configSchema.length > 0;
    if (
      !currentPlatformId ||
      configSchema.length === 0 ||
      !(Object.keys(configData).length > 0 || canInitItGlueEmpty || canInitSherwebEmpty)
    ) {
      return;
    }

    const derivedFromServer = formConfigFields.reduce((acc, field) => {
      let value = configData[field.key] ?? "";
      if (field.key === "api_secret_key" && !value && configData.api_secretKey) {
        value = String(configData.api_secretKey);
      }
      if (isItGluePlatform && field.key === "base_url" && !String(value || "").trim()) {
        value = IT_GLUE_DEFAULT_BASE_URL;
      }
      if (isSherwebPlatform && field.key === "gateway_base_url" && !String(value || "").trim()) {
        value = sherwebDefaultConfig.gateway_base_url;
      }
      acc[field.key] = value;
      return acc;
    }, {} as Record<string, string>);

    if (remoteSessionScriptConfigKeys) {
      derivedFromServer[remoteSessionScriptConfigKeys.windows] = String(
        configData[remoteSessionScriptConfigKeys.windows] ?? ""
      );
      derivedFromServer[remoteSessionScriptConfigKeys.mac] = String(
        configData[remoteSessionScriptConfigKeys.mac] ?? ""
      );
    }

    const platformChanged = currentPlatformId !== lastPlatformId;
    const formNeverSeeded = Object.keys(formValues).length === 0;
    const serverBacked = Object.keys(configData).length > 0 || canInitItGlueEmpty || canInitSherwebEmpty;
    /** After a fast partial seed (e.g. empty GET then full config), pull secrets from server without clobbering edits. */
    const isCredentialField = (field: { key: string; type?: string }) =>
      field.type === "password" ||
      field.key === "api_key" ||
      field.key.includes("secret");
    const serverCredentialsArrivedAfterEmptyBaseline =
      !hasUnsavedChanges &&
      Object.keys(configData).length > 0 &&
      configSchema.some((field) => {
        if (!isCredentialField(field)) return false;
        const fromServer = String(configData[field.key] ?? "").trim();
        const baseline = String(initialFormValues[field.key] ?? "").trim();
        return fromServer.length > 0 && baseline.length === 0;
      });

    const shouldInitialize =
      platformChanged ||
      (formNeverSeeded && serverBacked) ||
      serverCredentialsArrivedAfterEmptyBaseline;

    if (!shouldInitialize) {
      return;
    }

    setFormValues(derivedFromServer);
    setInitialFormValues(derivedFromServer);
    setHasUnsavedChanges(false);
    setTestSucceededAfterEdit(false);
    setSherwebInstructionsExpanded(false);
    setDattoInstructionsExpanded(false);

    if (platformChanged) {
      setLastPlatformId(currentPlatformId);
    }
  }, [
    platform?.id,
    configSchema,
    formConfigFields,
    configData,
    isItGluePlatform,
    isSherwebPlatform,
    remoteSessionScriptConfigKeys,
    hasUnsavedChanges,
    initialFormValues,
    lastPlatformId,
    formValues,
  ]);

  const handleConfigChange = (key: string, value: string) => {
    const newValues = { ...formValues, [key]: value };
    const changedKeys = Object.keys(newValues).filter(
      (k) => String(newValues[k] || "") !== String(initialFormValues[k] || "")
    );
    const hasChanges = changedKeys.length > 0;

    setFormValues(newValues);
    setHasUnsavedChanges(hasChanges);
    if (hasChanges) {
      setTestSucceededAfterEdit(false);
      setHaloPermissionFeedback(null);
      setItGlueAccessFeedback(null);
      if (customerId && platform?.name) {
        clearTestResultEntry(customerId, platform.name);
      }
    }
  };

  const { isGettingRefreshToken, startSignIn: handleStartNinjaOneSignIn } = useNinjaOneRefreshToken({
    instanceUrl: formValues.instance_url || "",
    clientId: formValues.client_id || "",
    clientSecret: formValues.client_secret || "",
    enabled: isNinjaOnePlatform,
    contextKey: platform?.id?.toString() || "",
    onStart: () => {
      setTestSucceededAfterEdit(false);
      setAlert((previous) => ({ ...previous, open: false }));
      if (customerId && platform?.name) clearTestResultEntry(customerId, platform.name);
    },
    onSuccess: (refreshToken) => {
      handleConfigChange("refresh_token", refreshToken);
      setAlert({ open: true, severity: "success", message: "Refresh Token added. Click Test to verify NinjaOne, then Save." });
    },
    onError: (message) => setAlert({ open: true, severity: "error", message }),
  });

  const handleSaveConfig = async () => {
    if (!customerId || !platform?.id) return;

    setIsSaving(true);
    try {
      const configToSave = supportsRmmScriptSelection ? { ...configData, ...formValues } : formValues;
      await updatePlatformConfig(platform.id.toString(), configToSave);

      setInitialFormValues(formValues);
      setHasUnsavedChanges(false);
      setTestSucceededAfterEdit(false);
      if (customerId && platform?.name) {
        clearTestResultEntry(customerId, platform.name);
      }

      setAlert({
        open: true,
        message: "Platform configuration saved successfully!",
        severity: "success",
      });

      setTimeout(async () => {
        try {
          if (userMspInfo?.msp_id) {
            getPlatforms(userMspInfo.msp_id);
          }
          
          const updatedConfig = await getPlatformConfig(platform.id.toString());
          if (updatedConfig) {
            setFetchedConfig(updatedConfig);
            
            const savedValues = formConfigFields.reduce((acc, field) => {
              let savedValue = updatedConfig[field.key] !== undefined
                ? String(updatedConfig[field.key] || "")
                : formValues[field.key] || "";
              // Datto: API may return api_secretKey (camelCase)
              if (field.key === "api_secret_key" && !savedValue && updatedConfig.api_secretKey) {
                savedValue = String(updatedConfig.api_secretKey);
              }
              acc[field.key] = savedValue;
              return acc;
            }, {} as Record<string, string>);

            if (remoteSessionScriptConfigKeys) {
              savedValues[remoteSessionScriptConfigKeys.windows] = String(
                updatedConfig[remoteSessionScriptConfigKeys.windows] ?? ""
              );
              savedValues[remoteSessionScriptConfigKeys.mac] = String(
                updatedConfig[remoteSessionScriptConfigKeys.mac] ?? ""
              );
            }
            
            setFormValues(savedValues);
            setInitialFormValues(savedValues);
          }
        } catch {
        }
      }, 100);
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.message || "Failed to save platform configuration",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenScriptSelectionModal = async () => {
    if (!customerId || !platform?.id || !platform?.name) return;
    if (!supportsRmmScriptSelection) {
      return;
    }
    if (!isPlatformConfigured) {
      setAlert({
        open: true,
        message: `Please configure ${rmmScriptProviderLabel} RMM credentials first before selecting ${rmmAutomationLabelPlural}.`,
        severity: "error",
      });
      return;
    }

    const requestId = ++selectionRequestRef.current;
    setModalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      const automations = await listRmmAutomations(isDattoPlatform ? "datto" : "ninjaone", platform.id.toString());
      if (selectionRequestRef.current !== requestId) return;
      setAllScripts(mapRmmAutomationsToListItems(automations));
      const existing = Array.isArray(configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY])
        ? configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY]
        : [];
      setSelectedScriptUids(existing.map((entry: { uid?: string }) => String(entry.uid || "")).filter(Boolean));
      setModalReverseSshWindowsUid(
        remoteSessionScriptConfigKeys
          ? String(formValues[remoteSessionScriptConfigKeys.windows] || configData[remoteSessionScriptConfigKeys.windows] || "")
          : ""
      );
      setModalReverseSshMacUid(
        remoteSessionScriptConfigKeys
          ? String(formValues[remoteSessionScriptConfigKeys.mac] || configData[remoteSessionScriptConfigKeys.mac] || "")
          : ""
      );
      setModalOpen(true);
    } catch (error: any) {
      if (selectionRequestRef.current !== requestId) return;
      console.error("Failed to fetch RMM automations:", error);
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to fetch available automations";
      setAlert({
        open: true,
        message: errorMessage.includes("not configured")
          ? `Please configure ${platform.name} credentials first before setting up ${rmmAutomationLabelPlural}`
          : errorMessage,
        severity: "error",
      });
    } finally {
      if (selectionRequestRef.current === requestId) setModalLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!customerId || !platform?.id) return;

    const hasConfigValues = Object.values(formValues).some(
      (value) => value && value.trim() !== ""
    );

    if (!hasConfigValues) {
      setAlert({
        open: true,
        message:
          "No configuration values to test. Please fill in the required fields first.",
        severity: "warning",
      });
      return;
    }

    setIsTesting(true);
    setHaloPermissionFeedback(null);
    setItGlueAccessFeedback(null);

    try {
      const result = await testPlatformConfiguration(
        customerId,
        platform.name,
        formValues
      );

      if (isHaloPlatform) {
        const permissionFeedback = await fetchHaloPermissionFeedback({
            url: String(formValues.url || "").trim(),
            client_id: String(formValues.client_id || "").trim(),
            client_secret: String(formValues.client_secret || "").trim(),
          });
        setHaloPermissionFeedback(permissionFeedback);
      }

      if (isItGluePlatform && result.details?.permission_checks?.length) {
        setItGlueAccessFeedback(buildItGlueAccessFeedback(result));
      }

      // Match Halo-style PSA flows: any successful test (including IT Glue "save OK" with
      // documentation-access warnings) unlocks Save whenever there are unsaved edits.
      if (result.success) {
        setTestSucceededAfterEdit(true);
      } else {
        setTestSucceededAfterEdit(false);
      }

      setAlert({
        open: true,
        message:
          result.message ||
          (result.success ? "Connection test successful!" : "Connection test failed."),
        severity: result.success ? "success" : "error",
      });
    } catch (error: any) {
      setTestSucceededAfterEdit(false);
      setAlert({
        open: true,
        message: error.message || "Connection test failed. Please check your configuration.",
        severity: "error",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const handleTriggerIngestion = async () => {
    if (!ingestionSourceDocType) return;
    setIsTriggeringIngestion(true);
    try {
      const integrationApi = new IntegrationApi();
      const resp = (await integrationApi.triggerIngestion(
        ingestionSourceDocType as "hudu_articles" | "itglue_docs" | "psa_tickets",
        ingestionPlatformId,
        userMspInfo?.msp_id
      )) as { alreadyRunning?: boolean; message?: string };
      const status = await integrationApi.getIngestionStatus(
        ingestionSourceDocType as "hudu_articles" | "itglue_docs" | "psa_tickets",
        ingestionPlatformId,
        userMspInfo?.msp_id
      );
      setIngestionStatus(status?.ingestion?.status || "RUNNING 0%");
      setIngestionDetails(readIngestionDetails(status?.ingestion));
      setIngestionCompletedAt(status?.ingestion?.completed_at || null);
      setAlert({
        open: true,
        message: resp?.message || "Ingestion started",
        severity: resp?.alreadyRunning ? "info" : "success",
      });
    } catch (error: any) {
      setAlert({
        open: true,
        message: error?.response?.data?.error || "Failed to trigger ingestion",
        severity: "error",
      });
    } finally {
      setIsTriggeringIngestion(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalError(null);
    setModalSuccess(null);
    setSelectedScriptUids([]);
    setModalReverseSshWindowsUid("");
    setModalReverseSshMacUid("");
  };

  const handleScriptSelect = (scriptUids: string[]) => {
    setSelectedScriptUids(scriptUids);
  };

  const handleModalConnect = async () => {
    if (!customerId || !platform?.id) return;

    setModalLoading(true);
    setModalError(null);

    try {
      const configWithSelection = {
        ...configData,
        ...formValues,
        ...(remoteSessionScriptConfigKeys
          ? {
              [remoteSessionScriptConfigKeys.windows]: modalReverseSshWindowsUid,
              [remoteSessionScriptConfigKeys.mac]: modalReverseSshMacUid,
            }
          : {}),
        [RMM_DISABLED_SCRIPT_COMPONENTS_KEY]: [
          ...(Array.isArray(configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY])
            ? configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY].filter((entry: { uid: string }) =>
                selectedScriptUids.includes(entry.uid) && !allScripts.some((automation) => automation.groupId === entry.uid))
            : []),
          ...allScripts
          .filter((script) => selectedScriptUids.includes(script.groupId))
          .map((script) => ({
            uid: script.groupId,
            name: script.name,
            ...(script.type ? { type: script.type } : {}),
          })),
        ],
      };

      await updatePlatformConfig(platform.id.toString(), configWithSelection);

      // Fetch updated config to refresh the UI
      const updatedConfig = await getPlatformConfig(platform.id.toString());
      setFetchedConfig(updatedConfig || {});

      if (remoteSessionScriptConfigKeys) {
        setFormValues((prev) => ({
          ...prev,
          [remoteSessionScriptConfigKeys.windows]: modalReverseSshWindowsUid,
          [remoteSessionScriptConfigKeys.mac]: modalReverseSshMacUid,
        }));
        setInitialFormValues((prev) => ({
          ...prev,
          [remoteSessionScriptConfigKeys.windows]: modalReverseSshWindowsUid,
          [remoteSessionScriptConfigKeys.mac]: modalReverseSshMacUid,
        }));
      }
      setRmmScriptsForDisplay(allScripts);

      // Refresh the platforms list
      if (userMspInfo?.msp_id) {
        await getPlatforms(userMspInfo.msp_id);
      }

      setModalSuccess(true);

      setAlert({
        open: true,
        message: "Platform configuration updated successfully!",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Failed to update platform configuration:", error);
      setModalError(error.message || "Failed to update platform configuration");
      setModalSuccess(false);
    } finally {
      setModalLoading(false);
    }
  };

  const handlePermissionUpdate = async (params: {
    customerId: string;
    applicationId: string;
    actionName: string;
    permission: "auto" | "approval" | "off";
  }) => {
    try {
      const action = safeActions.find((a: any) => a.name === params.actionName);

      if (!action || !action.id) {
        throw new Error("Action not found or missing ID");
      }

      await updatePlatformActionPermission(
        params.applicationId,
        action.id.toString(),
        params.permission
      );

      setAlert({
        open: true,
        message: "Permission updated successfully!",
        severity: "success",
      });
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to update permission. Please try again.";
      setAlert({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      throw error;
    }
  };

  const itGlueConnectionMessage = useMemo(() => {
    const compositeKey =
      customerId && platform?.name ? `${customerId}:${platform.name}` : null;
    const currentTestResult = compositeKey ? testResults[compositeKey] : null;
    return currentTestResult?.message;
  }, [testResults, customerId, platform?.name]);

  const dattoTestResult = isDattoPlatform && customerId && platform?.name
    ? testResults[`${customerId}:${platform.name}`]
    : undefined;

  const connectionStatus = useMemo(() => {
    const compositeKey =
      customerId && platform?.name ? `${customerId}:${platform.name}` : null;
    const currentTestResult = compositeKey ? testResults[compositeKey] : null;

    if (!currentTestResult) {
      return {
        status: "not_tested",
        label: "Not Tested",
        color: "#6B7280", // Gray
        show: true,
      };
    }

    if (currentTestResult.success) {
      return {
        status: "connected",
        label: currentTestResult.message || "Connection Successful",
        color: "#45BE51", // Green
        show: true,
      };
    } else {
      return {
        status: "failed",
        label: currentTestResult.message || "Connection Failed",
        color: "#FF6B6B", // Red
        show: true,
      };
    }
  }, [testResults, customerId, platform?.name]);

  const supportSnapshot = useMemo(
    () => ({
      surface: "platform_configuration" as const,
      title: platform?.name ? `${platform.name} platform configuration` : "Platform configuration",
      configuration: {
        customer_id: customerId || "",
        platform_name: platform?.name || "",
        platform_type: platform?.type || "",
        connection_status: connectionStatus.status,
        connection_message: connectionStatus.label,
        alert: alert.open ? { severity: alert.severity, message: alert.message } : null,
        halo_permission_feedback: haloPermissionFeedback,
        itglue_access_feedback: itGlueAccessFeedback,
        config: configData,
        permissions_loading: platformActionsLoading,
        permissions: safeActions.slice(0, 40).map((action: { name?: string; permission?: string }) => ({
          name: action.name,
          permission: action.permission || "",
        })),
        ingestion: supportsIngestions
          ? {
              source: ingestionSourceDocType,
              status: ingestionStatus,
              stage: ingestionDetails?.stage || null,
              completed_at: ingestionCompletedAt,
            }
          : null,
      },
    }),
    [
      platform?.name,
      platform?.type,
      customerId,
      connectionStatus.status,
      connectionStatus.label,
      alert,
      haloPermissionFeedback,
      itGlueAccessFeedback,
      configData,
      platformActionsLoading,
      safeActions,
      supportsIngestions,
      ingestionSourceDocType,
      ingestionStatus,
      ingestionDetails,
      ingestionCompletedAt,
    ]
  );
  usePublishConfigurationSupport("platform-configuration", supportSnapshot, 30);

  return (
    <Box sx={styles.pageContainer}>
      {/* Header */}
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerInfoContainer}>
          <IconButton onClick={() => navigate(-1)} sx={styles.backButton}>
            <img
              src={ArrowBackIcon}
              alt="Arrow Back"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
          <Box>
            <Typography sx={styles.headerTitle}>
              {formatAppName(platform?.name || "")} Configuration
            </Typography>
          </Box>
        </Box>
        <img
          src={getConnectorLogo(platform?.name || "")}
          alt={formatAppName(platform?.name || "")}
          style={styles.logo}
        />
      </Box>

      {/* Configuration Section */}
      <Paper sx={{ ...styles.paperContainer, mb: 2 }}>
        <Box
          sx={styles.sectionHeader}
          onClick={() => setConfigOpen(!configOpen)}
        >
          <Typography sx={styles.sectionTitle}>Configuration</Typography>
          <IconButton size="small" sx={styles.expandButton}>
            {configOpen ? (
              <img src={RemoveIcon} alt="Remove" />
            ) : (
              <img src={AddIcon} alt="Add" />
            )}
          </IconButton>
        </Box>

        {configOpen && (
          <Box sx={styles.configContent}>
            <Box sx={styles.gridContainer}>
              {isDattoPlatform && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <CollapsibleSetupGuidePanel
                    contentId="datto-platform-config-instructions"
                    expanded={dattoInstructionsExpanded}
                    onExpandedChange={setDattoInstructionsExpanded}
                  >
                    <DattoRmmGuideInner />
                  </CollapsibleSetupGuidePanel>
                </Box>
              )}
              {isSherwebPlatform && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <CollapsibleSetupGuidePanel
                    contentId="sherweb-platform-config-instructions"
                    expanded={sherwebInstructionsExpanded}
                    onExpandedChange={setSherwebInstructionsExpanded}
                  >
                    <SherwebSetupGuideSteps />
                  </CollapsibleSetupGuidePanel>
                </Box>
              )}
              {configSchema.length > 0 ? (
                configSchema.map((field) => (
                  <Box key={field.key}>
                    <Typography sx={styles.label}>{field.label}</Typography>
                    <TextField
                      sx={styles.textFieldWrapper}
                      fullWidth
                      type={field.type === "password" ? "password" : field.type || "text"}
                      placeholder={field.placeholder}
                      value={formValues[field.key] || ""}
                      disabled={isNinjaOnePlatform && (isGettingRefreshToken || isTesting || isSaving)}
                      onChange={(e) =>
                        handleConfigChange(field.key, e.target.value)
                      }
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: styles.textFieldInput,
                      }}
                    />
                    {field.helpText ? (
                      <Typography sx={{ fontSize: "12px", color: "#666F8D", mt: 0.75 }}>
                        {field.helpText}
                      </Typography>
                    ) : null}
                    {isNinjaOnePlatform && field.key === "refresh_token" ? (
                      <Button
                        variant="outlined"
                        onClick={handleStartNinjaOneSignIn}
                        disabled={
                          isGettingRefreshToken || isTesting || isSaving ||
                          !formValues.instance_url?.trim() || !formValues.client_id?.trim() || !formValues.client_secret?.trim()
                        }
                        sx={{ mt: 1, textTransform: "none", borderColor: "#705D4A", color: "#705D4A" }}
                      >
                        {isGettingRefreshToken ? "Waiting for NinjaOne..." : "Get Refresh Token"}
                      </Button>
                    ) : null}
                  </Box>
                ))
              ) : (
                <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 3 }}>
                  <Typography sx={{ color: "#666F8D", fontSize: "14px" }}>
                    No configuration fields available for this platform.
                  </Typography>
                </Box>
              )}
            </Box>

            {supportsRmmScriptSelection && (
              <Box sx={{ mt: 3, mb: 3 }}>
                {!isPlatformConfigured ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontSize: "14px", color: "#666F8D" }}>
                      Configure and test API credentials above to choose which {rmmScriptProviderLabel} {rmmAutomationLabelPlural} Theo should not run.
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      sx={{ textTransform: "none", color: "#9B7AEF", fontWeight: 700, flexShrink: 0 }}
                      onClick={() => setScriptExportDialogOpen(true)}
                    >
                      Export scripts
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={styles.label}>{isNinjaOneRmmPlatform ? "Automations" : "Scripts"}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          sx={{ textTransform: "none", color: "#9B7AEF", fontWeight: 700 }}
                          onClick={() => setScriptExportDialogOpen(true)}
                        >
                          Export scripts
                        </Button>
                        <Button
                          size="small"
                          sx={{ textTransform: "none", color: "#9B7AEF", fontWeight: 700 }}
                          onClick={() => handleOpenScriptSelectionModal()}
                          disabled={hasUnsavedChanges || isGettingRefreshToken || isTesting || isSaving || modalLoading}
                        >
                          Configure
                        </Button>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#F9FAFB",
                        borderRadius: "6px",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      {(() => {
                        const disabledScripts = Array.isArray(configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY])
                          ? (configData[RMM_DISABLED_SCRIPT_COMPONENTS_KEY] as Array<{
                              uid?: string;
                              name?: string;
                              type?: "ACTION" | "SCRIPT";
                            }>)
                          : [];
                        const resolveComponentName = (uid: string, storedName?: string) => {
                          if (storedName) {
                            return storedName;
                          }
                          return (
                            rmmScriptsForDisplay.find((script) => script.groupId === uid)?.name ||
                            uid
                          );
                        };
                        const reverseSshWindowsName = remoteSessionWindowsScriptUid
                          ? resolveComponentName(remoteSessionWindowsScriptUid)
                          : "";
                        const reverseSshMacName = remoteSessionMacScriptUid
                          ? resolveComponentName(remoteSessionMacScriptUid)
                          : "";
                        const disabledScriptNames = rmmDisabledScriptUids
                          .map((uid) => {
                            const stored = disabledScripts.find((entry) => entry.uid === uid);
                            const name = resolveComponentName(uid, stored?.name);
                            return isNinjaOneRmmPlatform && stored?.type === "ACTION" ? `${name} (Native action)` : name;
                          })
                          .filter(Boolean);

                        return (
                          <>
                            {supportsRmmScriptSelection ? (
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666F8D",
                                  mb: disabledScriptNames.length > 0 ? 1 : 0,
                                }}
                              >
                                <Box component="span" sx={{ fontWeight: 600, color: "#374151" }}>
                                  Windows secure shell:
                                </Box>
                                {remoteSessionWindowsScriptUid ? ` ${reverseSshWindowsName}` : " not configured"}
                                <br />
                                <Box component="span" sx={{ fontWeight: 600, color: "#374151" }}>
                                  macOS secure shell:
                                </Box>
                                {remoteSessionMacScriptUid ? ` ${reverseSshMacName}` : " not configured"}
                              </Typography>
                            ) : null}
                            {disabledScriptNames.length > 0 ? (
                              <Typography sx={{ fontSize: "14px", color: "#666F8D" }}>
                                <Box component="span" sx={{ fontWeight: 600, color: "#374151" }}>
                                  {`${disabledScriptNames.length} ${isNinjaOneRmmPlatform ? "automation" : "script"}${disabledScriptNames.length > 1 ? "s" : ""} disabled:`}
                                </Box>
                                {` ${disabledScriptNames.join(", ")}.`}
                              </Typography>
                            ) : null}
                          </>
                        );
                      })()}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {isPsaPlatform && isPlatformConfigured && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#F9FAFB",
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#666F8D" }}>
                    All queues and ticket types are imported by default.
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={styles.footerContainer}>
              {connectionStatus.show && !dattoTestResult && (
                <Chip
                  icon={
                    <Box
                      component="span"
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: connectionStatus.color,
                        mask: `url(${WarningInfo}) no-repeat center / contain`,
                        WebkitMask: `url(${WarningInfo}) no-repeat center / contain`,
                      }}
                    />
                  }
                  label={connectionStatus.label}
                  sx={{
                    ...styles.connectionChip,
                    backgroundColor: connectionStatus.status === "failed" ? "#FDECEC" : connectionStatus.status === "connected" ? "#D9FFDD" : "#F3F4F6",
                    borderColor: connectionStatus.status === "failed" ? "#F5B7B1" : connectionStatus.status === "connected" ? "#7AF086" : "#D1D5DB",
                  }}
                />
              )}
              <Box>
                <Button
                  variant="contained"
                  sx={styles.testButton}
                  onClick={handleTestConnection}
                  disabled={isTesting || isSaving || isGettingRefreshToken}
                  startIcon={
                    isTesting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isTesting ? "Testing..." : "Test"}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    ...styles.testButton,
                    mr: 1,
                  }}
                  onClick={handleSaveConfig}
                  disabled={
                    isSaving ||
                    isTesting ||
                    isGettingRefreshToken ||
                    !hasUnsavedChanges ||
                    !testSucceededAfterEdit
                  }
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
            {dattoTestResult ? <DattoAccessFeedbackAlert result={dattoTestResult} /> : null}
            {isHaloPlatform && haloPermissionFeedback ? (
              <HaloPermissionFeedbackAlert
                feedback={haloPermissionFeedback}
                onClose={() => setHaloPermissionFeedback(null)}
              />
            ) : null}
            {isItGluePlatform && itGlueAccessFeedback ? (
              <ItGlueAccessFeedbackAlert
                feedback={itGlueAccessFeedback}
                connectionMessage={itGlueConnectionMessage}
                onClose={() => setItGlueAccessFeedback(null)}
              />
            ) : null}
          </Box>
        )}
      </Paper>

      {/* Search — KB (Hudu / IT Glue) or ticket RAG (PSA psa_tickets: Autotask, Halo, ConnectWise): ingestion_metadata + refresh */}
      {supportsIngestions && (
        <Paper sx={{ ...styles.paperContainer, mb: 2 }}>
          <Box
            sx={styles.sectionHeader}
            onClick={() => setIngestionsOpen(!ingestionsOpen)}
          >
            <Typography sx={styles.sectionTitle}>Search</Typography>
            <IconButton size="small" sx={styles.expandButton}>
              {ingestionsOpen ? (
                <img src={RemoveIcon} alt="Remove" />
              ) : (
                <img src={AddIcon} alt="Add" />
              )}
            </IconButton>
          </Box>
          {ingestionsOpen && (
            <Box sx={styles.configContent}>
              {ingestionLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: "#705D4B" }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    backgroundColor: "#FAFAFA",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxWidth: "640px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1F2937", mb: 0.5 }}>
                        {ingestionCardTitle}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#666F8D" }}>
                        Status: {formatIngestionStatusLabel(ingestionStatus)}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#666F8D", mt: 0.25 }}>
                        Last run: {ingestionCompletedAt ? new Date(ingestionCompletedAt).toLocaleString() : "—"}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      sx={styles.testButton}
                      onClick={handleTriggerIngestion}
                      disabled={isTriggeringIngestion || String(ingestionStatus || "").toUpperCase().startsWith("RUNNING")}
                      startIcon={isTriggeringIngestion ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {isTriggeringIngestion ? "Starting..." : ingestionButtonLabel}
                    </Button>
                  </Box>
                  <IngestionRunDetails status={ingestionStatus} details={ingestionDetails} />
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Permissions Section */}
      <Paper sx={styles.paperContainer}>
        <Box
          sx={styles.sectionHeader}
          onClick={() => setPermissionsOpen(!permissionsOpen)}
        >
          <Typography sx={styles.sectionTitle}>Permissions</Typography>
          <IconButton size="small" sx={styles.expandButton}>
            {permissionsOpen ? (
              <img src={RemoveIcon} alt="Remove" />
            ) : (
              <img src={AddIcon} alt="Add" />
            )}
          </IconButton>
        </Box>
        {permissionsOpen && (
          <Box sx={{ ...styles.configContent, p: "8px" }}>
            {platformActionsLoading && <Typography>Loading permissions...</Typography>}
            {error && <Typography color="error">{error}</Typography>}
            {!platformActionsLoading && !error && (
              <IntegrationTable
                name={platform?.name || ""}
                logo={getConnectorLogo(platform?.name || "")}
                actions={safeActions}
                enabled={true}
                customerId={customerId || ""}
                applicationId={platform?.id?.toString() || ""}
                onToggle={() => {}}
                onPermissionUpdate={handlePermissionUpdate}
                disableEdit={platform?.msp_id === 0} // Disable editing for baseline platforms (msp_id = 0)
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={alert.severity === "success" ? 5000 : 4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <NeoConfigModal
        open={modalOpen}
        items={allScripts}
        selectedItemIds={selectedScriptUids}
        loading={modalLoading}
        error={modalError}
        success={modalSuccess}
        onSelect={handleScriptSelect}
        onClose={handleModalClose}
        onConnect={handleModalConnect}
        appName={formatAppName(platform?.name || "")}
        itemLabelPlural={rmmAutomationLabelPlural}
        selectionHeading={isNinjaOneRmmPlatform ? "Disable automations" : "Disable scripts"}
        selectionDescription={`Checked ${rmmAutomationLabelPlural} are unavailable to Theo.`}
        searchable
        searchPlaceholder={`Search ${rmmAutomationLabelPlural}`}
        dialogMaxWidth="md"
        topContentDivider={supportsRmmScriptSelection}
        topContent={
          supportsRmmScriptSelection ? (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--theo-font-sans)", color: "#000", mb: 0.5 }}>
                Secure shell
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#666F8D", mb: 2, fontFamily: "var(--theo-font-sans)" }}>
                {isNinjaOneRmmPlatform
                  ? "NinjaOne scripts Theo uses to open secure shells on Windows and macOS devices."
                  : "Datto components Theo uses to open secure shells on Windows and macOS devices."}
              </Typography>
              {[
                {
                  label: "Windows",
                  value: modalReverseSshWindowsUid,
                  onChange: setModalReverseSshWindowsUid,
                },
                {
                  label: "macOS",
                  value: modalReverseSshMacUid,
                  onChange: setModalReverseSshMacUid,
                },
              ].map((remoteSessionOption) => (
                <Box key={remoteSessionOption.label} sx={{ mb: remoteSessionOption.label === "Windows" ? 2 : 0 }}>
                  <RmmRemoteSessionScriptSelect
                    label={remoteSessionOption.label}
                    value={remoteSessionOption.value}
                    onChange={remoteSessionOption.onChange}
                    scripts={allScripts.filter((automation) => automation.type !== "ACTION")}
                    storedScripts={rmmScriptsForDisplay}
                    disabled={modalLoading}
                  />
                </Box>
              ))}
            </Box>
          ) : undefined
        }
      />
      {supportsRmmScriptSelection ? (
        <DattoScriptExportDialog
          provider={isNinjaOneRmmPlatform ? "ninjaone" : "datto"}
          open={scriptExportDialogOpen}
          onClose={() => setScriptExportDialogOpen(false)}
        />
      ) : null}
    </Box>
  );
};

export default PlatformConfiguration;
